import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Divider, Stack, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import HeatingDataPropType from '@/components/HeatingAssistant/lib/HeatingDataPropType';
import Checkbox from '@/components/ui/CheckboxWithLabel/CheckboxWithLabel';
import Input from '@/components/ui/Input/Input';
import OnlyDesktop from '@/components/ui/OnlyDesktop/OnlyDesktop';
import OnlyMobile from '@/components/ui/OnlyMobile/OnlyMobile';
import { isDigit } from '@/helpers/validators';
import useFormatMessage from '@/hooks/useFormatMessage';

import { heatingTypes } from '../../lib/constants';
import {
  Button,
  Checkboxes,
  DesktopContentWrapper,
  Duration,
  MobileContentWrapper,
} from './Start.style';

const getValidationSchema = formatMessage =>
  Yup.object({
    heatingType: Yup.object({
      gas: Yup.boolean(),
      oil: Yup.boolean(),
      electro: Yup.boolean(),
    }).test('atLeastOneChecked', null, heatingType => {
      return Object.values(heatingType).includes(true);
    }),
    power: Yup.string()
      .test(
        'validDecimal',
        formatMessage('heating_default_amounterror'),
        value => isDigit(value)
      )
      .required(''),
  });

/**
 * Heating Assistant Step Start.
 */

const Start = ({ data: { heatingType, power }, updateData }) => {
  const formatMessage = useFormatMessage();

  const {
    handleSubmit,
    control,
    formState: { isValid },
    watch,
    setValue,
  } = useForm({
    defaultValues: { heatingType, power },
    mode: 'onChange',
    resolver: yupResolver(getValidationSchema(formatMessage)),
  });

  useEffect(() => {
    setValue('heatingType', heatingType, { shouldValidate: true });
  }, [heatingType, setValue]);

  const heatingTypeWatched = watch('heatingType');

  const onSubmit = () => {
    updateData({ activeStep: 7, flow: 'direct' });
  };

  return (
    <>
      <OnlyMobile unmountOnHide>
        <Stack justifyContent="center" direction="row">
          <MobileContentWrapper>
            <Button
              color="primaryLight"
              size="large"
              fullWidth
              data-test-subject="wizard-mobile-button"
              onClick={() => updateData({ activeStep: 1, flow: 'wizard' })}>
              {formatMessage('heating_calculator')}
            </Button>
            <Box width="100%" my={2}>
              <Divider>
                <Typography variant="body2">
                  {formatMessage('heating_default_divider')}
                </Typography>
              </Divider>
            </Box>
            <Button
              size="large"
              fullWidth
              data-test-subject="direct-mobile-button"
              onClick={() => updateData({ activeStep: 7, flow: 'direct' })}>
              {formatMessage('heating_directrequest')}
            </Button>
          </MobileContentWrapper>
        </Stack>
      </OnlyMobile>
      <OnlyDesktop unmountOnHide>
        <Stack justifyContent="center" direction="row">
          <DesktopContentWrapper>
            <Typography fontStyle="normal" variant="h3" component="h2" mb={3}>
              {formatMessage('heating_default_head')}
            </Typography>
            <Button
              color="primaryLight"
              size="large"
              sx={{ marginTop: 2 }}
              data-test-subject="wizard-button"
              onClick={() => updateData({ activeStep: 1, flow: 'wizard' })}>
              {formatMessage('heating_default_calculation')}
            </Button>
            <Duration align="center" variant="body1">
              {formatMessage('heating_default_duration')}
            </Duration>
            <Box width="100%" mt={1}>
              <Divider>
                <Typography variant="body2">
                  {formatMessage('heating_default_dr')}
                </Typography>
              </Divider>
            </Box>
            <form noValidate onSubmit={handleSubmit(onSubmit)}>
              <Stack alignItems="center" direction="column">
                <Typography my={6} align="center" variant="body1">
                  {formatMessage('heating_default_amount')}
                </Typography>
                <Controller
                  name="power"
                  control={control}
                  render={({
                    field: { ref, name, onChange, ...field },
                    fieldState: { error },
                  }) => (
                    <Input
                      data-test-subject="heating-capacity-input"
                      label={formatMessage('heating_default_amountfield')}
                      error={!!error}
                      inputRef={ref}
                      required
                      helperText={error?.message}
                      name={name}
                      onChange={e => {
                        updateData({ power: e.target.value });
                        onChange(e);
                      }}
                      inputMode="numeric"
                      {...field}
                    />
                  )}
                />
                <Typography mt={4.5} mb={3.5} align="center" variant="body1">
                  {formatMessage('heating_default_kind')}
                </Typography>
                <Checkboxes>
                  {heatingTypes.map(type => (
                    <Controller
                      key={type}
                      name={`heatingType.${type}`}
                      control={control}
                      render={({
                        field: { ref, value, onChange, ...field },
                        fieldState: { error },
                      }) => (
                        <Checkbox
                          label={formatMessage(`heating_default_${type}`)}
                          inputRef={ref}
                          checked={value}
                          error={!!error}
                          onChange={e => {
                            updateData({
                              heatingType: {
                                ...heatingTypeWatched,
                                [type]: e.target.checked,
                              },
                            });
                            onChange(e);
                          }}
                          data-test-subject={`${type}-checkbox`}
                          {...field}
                        />
                      )}
                    />
                  ))}
                </Checkboxes>
                <Button
                  type="submit"
                  size="large"
                  sx={{ marginTop: 2 }}
                  data-test-subject="direct-button"
                  disabled={!isValid}>
                  {formatMessage('heating_default_next')}
                </Button>
              </Stack>
            </form>
          </DesktopContentWrapper>
        </Stack>
      </OnlyDesktop>
    </>
  );
};

Start.propTypes = {
  /** Heating assistant data. */
  data: HeatingDataPropType.isRequired,
  /** Method to update heating assistant data. */
  updateData: PropTypes.func.isRequired,
};

export default Start;
